import React, { useState, useEffect } from 'react';
import { Folder, FolderPlus, ChevronRight, Home, Check, FileText, Plus } from 'lucide-react';

const SaveLocationModal = ({ isOpen, onClose, onConfirm, folders, articles = [], initialFolderId, onCreateFolder, title = "儲存文章", confirmText = "儲存至此" }) => {
    const [currentFolderId, setCurrentFolderId] = useState(initialFolderId || null);
    const [isCreating, setIsCreating] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');

    // Reset to initial folder when modal opens
    useEffect(() => {
        if (isOpen) {
            setCurrentFolderId(initialFolderId || null);
            setIsCreating(false);
            setNewFolderName('');
        }
    }, [isOpen, initialFolderId]);

    if (!isOpen) return null;

    const currentFolder = folders.find(f => f.id === currentFolderId);
    const subFolders = folders.filter(f => (f.parentId || null) === (currentFolderId || null));
    const currentArticles = articles.filter(a => (a.folderId || null) === (currentFolderId || null));

    // Breadcrumb navigation logic
    const getBreadcrumbs = () => {
        const crumbs = [];
        let curr = currentFolder;
        while (curr) {
            crumbs.unshift(curr);
            curr = folders.find(f => f.id === curr.parentId);
        }
        return crumbs;
    };

    const breadcrumbs = getBreadcrumbs();

    const handleCreateClick = () => {
        if (!newFolderName.trim()) return;
        onCreateFolder(newFolderName, currentFolderId);
        setNewFolderName('');
        setIsCreating(false);
    };

    return (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl flex flex-col h-[600px] overflow-hidden border border-slate-200 dark:border-slate-700">

                {/* Header */}
                <div className="p-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-900/50">
                    <h3 className="font-bold text-slate-800 dark:text-slate-100 text-lg">{title}</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                        <span className="text-sm">取消</span>
                    </button>
                </div>

                {/* Breadcrumbs */}
                <div className="px-4 py-3 bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700 flex items-center gap-1 overflow-x-auto text-sm">
                    <button
                        onClick={() => setCurrentFolderId(null)}
                        className={`flex items - center gap - 1 hover: bg - slate - 100 dark: hover: bg - slate - 700 px - 2 py - 1 rounded transition - colors ${!currentFolderId ? 'font-bold text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'} `}
                    >
                        <Home className="w-4 h-4" />
                        <span>我的文章庫</span>
                    </button>
                    {breadcrumbs.map((folder, index) => (
                        <React.Fragment key={folder.id}>
                            <ChevronRight className="w-4 h-4 text-slate-300 dark:text-slate-600 flex-shrink-0" />
                            <button
                                onClick={() => setCurrentFolderId(folder.id)}
                                className={`whitespace - nowrap hover: bg - slate - 100 dark: hover: bg - slate - 700 px - 2 py - 1 rounded transition - colors ${index === breadcrumbs.length - 1 ? 'font-bold text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'} `}
                            >
                                {folder.name}
                            </button>
                        </React.Fragment>
                    ))}
                </div>

                {/* Folder & File List */}
                <div className="flex-1 overflow-y-auto p-4 bg-slate-50 dark:bg-slate-900/30">
                    {subFolders.length === 0 && currentArticles.length === 0 && !isCreating ? (
                        <div className="h-full flex flex-col items-center justify-center text-slate-400 dark:text-slate-500">
                            <Folder className="w-12 h-12 mb-2 opacity-20" />
                            <p>此資料夾是空的</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                            {/* New Folder Input */}
                            {isCreating && (
                                <div className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white dark:bg-slate-800 ring-2 ring-indigo-500 shadow-lg">
                                    <Folder className="w-10 h-10 text-indigo-400 dark:text-indigo-500 fill-indigo-100 dark:fill-indigo-900" />
                                    <input
                                        autoFocus
                                        type="text"
                                        value={newFolderName}
                                        onChange={(e) => setNewFolderName(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleCreateClick(); if (e.key === 'Escape') setIsCreating(false); }}
                                        onBlur={() => { if (!newFolderName) setIsCreating(false); }}
                                        placeholder="資料夾名稱"
                                        className="w-full text-xs text-center border-b border-indigo-300 focus:border-indigo-600 outline-none bg-transparent"
                                    />
                                </div>
                            )}

                            {/* Folders */}
                            {subFolders.map(folder => (
                                <div
                                    key={folder.id}
                                    onClick={() => setCurrentFolderId(folder.id)}
                                    className="flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-900/20 hover:ring-1 hover:ring-indigo-200 dark:hover:ring-indigo-800 cursor-pointer transition-all group text-center"
                                >
                                    <Folder className="w-10 h-10 text-indigo-400 dark:text-indigo-500 fill-indigo-100 dark:fill-indigo-900 group-hover:scale-110 transition-transform" />
                                    <span className="text-xs font-medium text-slate-700 dark:text-slate-300 line-clamp-2 w-full break-words">{folder.name}</span>
                                </div>
                            ))}

                            {/* Files (Read-only visual) */}
                            {currentArticles.map(article => (
                                <div
                                    key={article.id}
                                    className="flex flex-col items-center gap-2 p-4 rounded-xl opacity-60 grayscale hover:grayscale-0 hover:opacity-100 transition-all text-center cursor-default"
                                    title="現有檔案"
                                >
                                    <FileText className="w-10 h-10 text-slate-400 dark:text-slate-500 fill-slate-100 dark:fill-slate-800" />
                                    <span className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2 w-full break-words">{article.title}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-100 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-between gap-4">
                    <button
                        onClick={() => setIsCreating(true)}
                        className="px-4 py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                    >
                        <FolderPlus className="w-4 h-4" />
                        <span>新增資料夾</span>
                    </button>

                    <button
                        onClick={() => onConfirm(currentFolderId)}
                        className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-200 dark:shadow-none transition-all flex items-center gap-2"
                    >
                        <Check className="w-4 h-4" />
                        <span>{confirmText}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SaveLocationModal;
